import { useCallback, useEffect, useState } from 'react'
import { Link, Outlet, useNavigate } from 'react-router-dom'
import { FileText, FlaskConical, LifeBuoy, LogOut, Megaphone, Settings } from 'lucide-react'
import { Button } from '@components/ui/button'
import { fetchFromApi } from '@utils/apiOrigin'
import { getTicketAdminHeaders } from '@utils/ticketLabels'
import { setDemoMode, useDemoMode } from '@utils/demoMode'

const ADMIN_TOKEN_KEY = 'ecofarte_admin_token'
const ADMIN_SESSION_KEY = 'ecofarte_admin_session'
const POLL_MS = 60 * 1000

export function AdminLayout() {
  const navigate = useNavigate()
  const [pendingCount, setPendingCount] = useState(0)
  const demoMode = useDemoMode()

  const refreshPending = useCallback(async () => {
    try {
      const res = await fetchFromApi('/api/admin/tickets/unread-count', { headers: getTicketAdminHeaders() })
      if (!res.ok) return
      const { data } = await res.json()
      setPendingCount(data.count || 0)
    } catch {
      // sin badge si falla el polling
    }
  }, [])

  useEffect(() => {
    void refreshPending()
    const timer = setInterval(() => void refreshPending(), POLL_MS)
    return () => clearInterval(timer)
  }, [refreshPending])

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY)
    localStorage.removeItem(ADMIN_SESSION_KEY)
    navigate('/admin', { replace: true })
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="border-b bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <span className="font-semibold text-cofarte-purple">Administración eCOFARTE</span>
            <nav className="flex items-center gap-3 text-sm">
              <Link
                to="/admin/documentos"
                className="inline-flex items-center gap-1.5 text-gray-700 hover:text-cofarte-purple"
              >
                <FileText className="h-4 w-4" />
                Gestor documental
              </Link>
              <Link
                to="/admin/tickets"
                className="relative inline-flex items-center gap-1.5 text-gray-700 hover:text-cofarte-purple"
              >
                <LifeBuoy className="h-4 w-4" />
                Tickets
                {pendingCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">
                    {pendingCount > 99 ? '99+' : pendingCount}
                  </span>
                )}
              </Link>
              <Link
                to="/admin/cambios"
                className="inline-flex items-center gap-1.5 text-gray-700 hover:text-cofarte-purple"
              >
                <Megaphone className="h-4 w-4" />
                Cambios
              </Link>
              <Link
                to="/admin/config"
                className="inline-flex items-center gap-1.5 text-gray-700 hover:text-cofarte-purple"
              >
                <Settings className="h-4 w-4" />
                Configuración
              </Link>
            </nav>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setDemoMode(!demoMode)}
              title={demoMode
                ? 'Modo demostración activo en este navegador: los datos del cliente se muestran como DEMOSTRACIÓN. Pulse para desactivar.'
                : 'Activa el modo demostración solo en este navegador: al entrar como socio, el nombre del cliente se mostrará como DEMOSTRACIÓN.'}
              className={demoMode
                ? 'inline-flex items-center gap-1.5 rounded-lg border border-amber-400 bg-amber-50 px-3 py-1.5 text-sm font-semibold text-amber-700 hover:bg-amber-100 transition-colors'
                : 'inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors'}
            >
              <FlaskConical className="h-4 w-4" />
              Modo demo: {demoMode ? 'ON' : 'OFF'}
            </button>
            <Button type="button" variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-1.5" />
              Cerrar sesión
            </Button>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-6">
        <Outlet />
      </main>
    </div>
  )
}
